/**
 * WeekCalendar Component
 * 7-day calendar view showing pilot's trips
 * Shows Today + 6 days forward, scrollable on mobile
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import CalendarDayCell from './CalendarDayCell';
import { Trip } from '@/services/tripService';

interface WeekCalendarProps {
  trips: Trip[];
  onTripClick?: (tripId: number) => void;
  onDayClick?: (date: Date) => void;
  className?: string;
}

// Helper to get start of day in local timezone
function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Helper to check if two dates are the same day
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// Helper to check if a date falls within a trip's date range
function tripIncludesDate(trip: Trip, date: Date): boolean {
  const tripStart = startOfDay(new Date(trip.startDate));
  const tripEnd = startOfDay(new Date(trip.endDate));
  const checkDate = startOfDay(date);

  return checkDate >= tripStart && checkDate <= tripEnd;
}

// Get the route for a trip - first departure to last arrival across all flights
function getTripRoute(trip: Trip): { fromAirport?: string; toAirport?: string } {
  if (trip.flights.length === 0) {
    return {};
  }

  // Sort flights by leg number
  const sorted = [...trip.flights].sort((a, b) => a.legNumber - b.legNumber);

  return {
    fromAirport: sorted[0].fromAirport || undefined,
    toAirport: sorted[sorted.length - 1].toAirport || undefined,
  };
}

export const WeekCalendar: React.FC<WeekCalendarProps> = ({
  trips,
  onTripClick,
  onDayClick,
  className,
}) => {
  const navigate = useNavigate();
  const today = useMemo(() => startOfDay(new Date()), []);

  // Generate 7 days starting from today
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(today);
      day.setDate(today.getDate() + i);
      days.push(day);
    }
    return days;
  }, [today]);

  // Map trips to their calendar days
  const tripsByDay = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        id: number;
        qNumber: string;
        status: string;
        fromAirport?: string;
        toAirport?: string;
      }>
    >();

    weekDays.forEach((day) => {
      const dateKey = day.toISOString().split('T')[0];
      const tripsForDay = trips
        .filter((trip) => tripIncludesDate(trip, day))
        .map((trip) => {
          // Use the trip's overall route (first departure → last arrival)
          const route = getTripRoute(trip);
          return {
            id: trip.id,
            qNumber: trip.qNumber,
            status: trip.status,
            fromAirport: route.fromAirport,
            toAirport: route.toAirport,
          };
        });
      map.set(dateKey, tripsForDay);
    });

    return map;
  }, [weekDays, trips]);

  const handleTripClick = (tripId: number) => {
    if (onTripClick) {
      onTripClick(tripId);
    } else {
      navigate(`/trips/${tripId}`);
    }
  };

  const handleDayClick = (date: Date) => {
    if (onDayClick) {
      onDayClick(date);
    }
  };

  // Get month label for the week
  const monthLabel = useMemo(() => {
    const firstDay = weekDays[0];
    const lastDay = weekDays[6];

    if (firstDay.getMonth() === lastDay.getMonth()) {
      return firstDay.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }

    // Week spans two months
    return `${firstDay.toLocaleDateString('en-US', { month: 'short' })} - ${lastDay.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  }, [weekDays]);

  return (
    <div className={cn('bg-card rounded-lg shadow border border-border', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Your Week</h2>
        <p className="text-sm text-muted-foreground">{monthLabel}</p>
      </div>

      {/* Calendar Grid - Scrollable on mobile */}
      <div className="overflow-x-auto">
        <div className="flex min-w-max">
          {weekDays.map((day) => {
            const dateKey = day.toISOString().split('T')[0];
            const tripsForDay = tripsByDay.get(dateKey) || [];

            return (
              <CalendarDayCell
                key={dateKey}
                date={day}
                isToday={isSameDay(day, today)}
                trips={tripsForDay}
                onTripClick={handleTripClick}
                onDayClick={handleDayClick}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeekCalendar;
